import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  IconButton,
  Typography,
  CircularProgress,
  Grid,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useTranslation } from "react-i18next";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

const SaleAdd = ({ open, onClose, token }) => {
  const { t } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd("shop/sales", token);

  // Fetch dropdown data for shops, customers, units, and currencies.
  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop", "nopagination", "v2"],
    "shop/shop/?page_size=all",
    token
  );
  const { data: customers, loading: customerLoading } = useFetchObjects(
    ["customer", "nopagination", "v2"],
    "shop/customers/?page_size=all",
    token
  );
  const { data: units, loading: unitLoading } = useFetchObjects(
    ["unit", "nopagination", "v2"],
    "shop/units/?page_size=all",
    token
  );
  const { data: currencies, loading: currencyLoading } = useFetchObjects(
    ["currency", "nopagination", "v2"],
    "shop/currencies/?page_size=all",
    token
  );

  // Fetch stocks for the selected shop. Always send shop parameter.
  const [shop, setShop] = useState("");
  const [stocks, setStocks] = useState([]);
  const {
    data: stocksData,
    loading: stockLoading,
  } = useFetchObjects(
    ["stock", "sale", "nopagination", "v2", shop],
    shop ? `shop/stocks/?shop=${shop}&page_size=all` : null,
    token
  );

  useEffect(() => {
    if (stocksData) {
      const stocksList = Array.isArray(stocksData) ? stocksData : (stocksData?.results || []);
      setStocks(stocksList);
    } else {
      setStocks([]);
    }
  }, [stocksData]);

  const shopsList = Array.isArray(shopsData) ? shopsData : (shopsData?.results || []);
  const customersList = Array.isArray(customers) ? customers : (customers?.results || []);
  const unitsList = Array.isArray(units) ? units : (units?.results || []);
  const currenciesList = Array.isArray(currencies) ? currencies : (currencies?.results || []);

  console.log('Shop selected:', shop);
  console.log('Shops list:', shopsList);
  console.log('Stocks data:', stocksData);
  console.log('Customers data:', customers);
  console.log('Customers list length:', customersList.length);

  // Auto-select shop if only one exists.
  useEffect(() => {
    if (shopsList.length === 1 && !shop) {
      setShop(String(shopsList[0].id));
    }
  }, [shopsList, shop]);

  // Derive unique products from stocks using purchase_item_details.
  const uniqueProducts = useMemo(() => {
    const stocksList = Array.isArray(stocks) ? stocks : [];
    const prodMap = new Map();
    stocksList.forEach((stock) => {
      if (
        stock.purchase_item_details &&
        stock.purchase_item_details.product &&
        stock.purchase_item_details.product.id
      ) {
        const prod = stock.purchase_item_details.product;
        if (!prodMap.has(prod.id)) {
          prodMap.set(prod.id, prod);
        }
      }
    });
    const products = Array.from(prodMap.values());
    console.log('Stocks count:', stocksList.length);
    console.log('Unique products count:', products.length);
    return products;
  }, [stocks]);

  // Local state for sale-level fields.
  const [customerSelected, setCustomerSelected] = useState("");
  const [saleDate, setSaleDate] = useState(""); // date only
  const [saleCurrency, setSaleCurrency] = useState(""); // sale-level currency
  const [discount, setDiscount] = useState("");
  const [receivedAmount, setReceivedAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [formError, setFormError] = useState("");
  const [errors, setErrors] = useState({});

  // Add a new sale item row.
  const addItem = () => {
    setItems([
      ...items,
      {
        product: "",
        unit: "",
        available: "",
        quantity: "",
        sale_price: "",
      },
    ]);
  };

  // Remove an item row.
  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  // When a product is selected from Autocomplete, auto-fill unit, sale_price, and available quantity.
  const handleProductSelect = (index, selectedProduct) => {
    const newItems = [...items];
    newItems[index].product = selectedProduct ? String(selectedProduct.id) : "";
    if (selectedProduct) {
      const stockRecord = stocks.find(
        (stock) =>
          stock.purchase_item_details &&
          stock.purchase_item_details.product &&
          String(stock.purchase_item_details.product.id) ===
            String(selectedProduct.id)
      );
      if (stockRecord) {
        // Use the unit from stock.unit_details instead of purchase_item_details.unit.
        newItems[index].unit = stockRecord.unit_details
          ? String(stockRecord.unit_details.id)
          : "";
        newItems[index].sale_price = stockRecord.sale_price || "";
        newItems[index].available = stockRecord.quantity;
      } else {
        newItems[index].unit = "";
        newItems[index].sale_price = "";
        newItems[index].available = "0";
      }
    } else {
      newItems[index].unit = "";
      newItems[index].sale_price = "";
      newItems[index].available = "0";
    }
    setItems(newItems);
  };

  // Update a sale item field.
  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  // Calculate total sale amount.
  const totalAmount = items.reduce((acc, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.sale_price) || 0;
    return acc + qty * price;
  }, 0);

  const discountAmount = (totalAmount * (parseFloat(discount) || 0)) / 100;
  const finalTotal = totalAmount - discountAmount;

  // Validate form fields.
  const validate = () => {
    let newErrors = {};
    if (!shop) newErrors.shop = t("sale.shopRequired") || "Shop is required";
    if (!customerSelected)
      newErrors.customer = t("sale.customerRequired") || "Customer is required";
    if (!saleDate)
      newErrors.saleDate =
        t("sale.saleDateRequired") || "Sale date is required";
    if (!saleCurrency)
      newErrors.currency = t("sale.currencyRequired") || "Currency is required";
    if (items.length === 0) {
      newErrors.items =
        t("sale.atLeastOneItemRequired") ||
        "At least one sale item is required";
    } else {
      items.forEach((item, idx) => {
        if (!item.product)
          newErrors[`item_${idx}_product`] =
            t("sale.productRequired") || "Product is required";
        if (!item.unit)
          newErrors[`item_${idx}_unit`] =
            t("sale.unitRequired") || "Unit is required";
        if (!item.quantity)
          newErrors[`item_${idx}_quantity`] =
            t("sale.quantityRequired") || "Quantity is required";
        else if (parseFloat(item.quantity) > parseFloat(item.available || "0"))
          newErrors[`item_${idx}_quantity`] =
            t("sale.quantityExceedsAvailable") ||
            "Quantity exceeds available stock";
        if (!item.sale_price)
          newErrors[`item_${idx}_salePrice`] =
            t("sale.salePriceRequired") || "Sale price is required";
      });
    }
    if (receivedAmount !== "" && parseFloat(receivedAmount) > finalTotal) {
      newErrors.receivedAmount =
        t("sale.receivedExceedsTotal") ||
        "Received amount should not exceed total amount";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const payload = {
      shop,
      customer: customerSelected,
      discount: discountAmount,
      received_amount: receivedAmount,
      sale_date: saleDate,
      currency: saleCurrency,
      notes: notes.trim() || undefined,
      items,
    };
    handleAdd(payload);
  };

  useEffect(() => {
    if (resetForm) {
      setShop("");
      setCustomerSelected("");
      setDiscount("");
      setReceivedAmount("");
      setNotes("");
      setSaleDate("");
      setSaleCurrency("");
      setItems([]);
      setErrors({});
      setFormError("");
      onClose();
    }
  }, [resetForm, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{t("sale.addSale")}</DialogTitle>
      <DialogContent>
        {formError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {formError}
          </Typography>
        )}
        <Box
          component="form"
          noValidate
          sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}
        >
          {/* Sale-level fields arranged in two columns */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                select
                label={t("sale.selectShop")}
                fullWidth
                required
                value={shop}
                onChange={(e) => setShop(e.target.value)}
                disabled={shopLoading}
                error={Boolean(errors.shop)}
                helperText={errors.shop}
              >
                {shopsList.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={customersList}
                getOptionLabel={(option) => option.name || ""}
                getOptionKey={(option) => option.id}
                value={customersList.find((c) => String(c.id) === customerSelected) || null}
                onChange={(event, newValue) => setCustomerSelected(newValue ? String(newValue.id) : "")}
                loading={customerLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("sale.selectCustomer")}
                    required
                    error={Boolean(errors.customer)}
                    helperText={errors.customer}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t("sale.saleDate")}
                type="date"
                fullWidth
                required
                value={saleDate}
                onChange={(e) => setSaleDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors.saleDate)}
                helperText={errors.saleDate}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label={t("sale.currency")}
                fullWidth
                required
                value={saleCurrency}
                onChange={(e) => setSaleCurrency(e.target.value)}
                disabled={currencyLoading}
                error={Boolean(errors.currency)}
                helperText={errors.currency}
              >
                {currenciesList.map((curr) => (
                  <MenuItem key={curr.id} value={String(curr.id)}>
                    {curr.title}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={`${t("sale.discount")} %`}
                type="number"
                fullWidth
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                error={Boolean(errors.discount)}
                helperText={errors.discount}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("sale.note")}
                fullWidth
                multiline
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("sale.notePlaceholder")}
              />
            </Grid>
          </Grid>
          {/* Sale Items Section */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6">{t("sale.saleItems")}</Typography>
            {errors.items && (
              <Typography color="error" variant="body2">
                {errors.items}
              </Typography>
            )}
            {items.map((item, index) => {
              const selectedIds = items.map((i) => i.product);
              const filteredOptions = Array.isArray(uniqueProducts) 
                ? uniqueProducts.filter(
                    (prod) =>
                      String(prod.id) === item.product ||
                      !selectedIds.includes(String(prod.id))
                  )
                : [];
              return (
                <Box
                  key={index}
                  sx={{
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    p: 1,
                    mb: 1,
                  }}
                >
                  {/* Row 1: Autocomplete product select and Remove icon */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Autocomplete
                      options={filteredOptions}
                      getOptionLabel={(option) => option.name}
                      getOptionKey={(option) => option.id}
                      value={
                        filteredOptions.find(
                          (p) => String(p.id) === item.product
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        handleProductSelect(index, newValue ? newValue : null)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("sale.selectProduct")}
                          required
                          error={Boolean(errors[`item_${index}_product`])}
                          helperText={errors[`item_${index}_product`]}
                        />
                      )}
                      sx={{ flex: 2 }}
                    />
                    <IconButton onClick={() => removeItem(index)} color="error">
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  </Box>
                  {/* Row 2: All sale item fields in one row */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      label={t("sale.availableQuantity")}
                      value={item.available || ""}
                      sx={{ flex: 1 }}
                      InputProps={{ readOnly: true }}
                    />
                    <TextField
                      label={t("sale.quantity")}
                      type="number"
                      required
                      value={item.quantity}
                      onChange={(e) =>
                        handleItemChange(index, "quantity", e.target.value)
                      }
                      sx={{ flex: 1 }}
                      error={Boolean(errors[`item_${index}_quantity`])}
                      helperText={errors[`item_${index}_quantity`]}
                    />
                    <TextField
                      select
                      label={t("sale.selectUnit")}
                      required
                      value={item.unit}
                      onChange={(e) =>
                        handleItemChange(index, "unit", e.target.value)
                      }
                      sx={{ flex: 1 }}
                      disabled={unitLoading}
                      error={Boolean(errors[`item_${index}_unit`])}
                      helperText={errors[`item_${index}_unit`]}
                    >
                      {unitsList.map((unit) => (
                        <MenuItem key={unit.id} value={String(unit.id)}>
                          {unit.title}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label={t("sale.salePrice")}
                      type="number"
                      required
                      value={item.sale_price}
                      onChange={(e) =>
                        handleItemChange(index, "sale_price", e.target.value)
                      }
                      sx={{ flex: 1 }}
                      error={Boolean(errors[`item_${index}_salePrice`])}
                      helperText={errors[`item_${index}_salePrice`]}
                    />
                  </Box>
                </Box>
              );
            })}
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={addItem}
              sx={{ mt: 1 }}
            >
              {t("sale.addItem")}
            </Button>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1">
              {t("sale.subtotal")}: {totalAmount.toFixed(2)}
            </Typography>
            {discountAmount > 0 && (
              <Typography variant="body1">
                {t("sale.discountAmount")}: -{discountAmount.toFixed(2)}
              </Typography>
            )}
            <Typography variant="h6">
              {t("sale.totalAmount")}: {finalTotal.toFixed(2)}
            </Typography>

            <Grid item xs={6}>
              <TextField
                label={t("sale.receivedAmount")}
                type="number"
                fullWidth
                value={receivedAmount}
                onChange={(e) => setReceivedAmount(e.target.value)}
                error={Boolean(errors.receivedAmount)}
                helperText={errors.receivedAmount}
              />
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("sale.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("sale.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaleAdd;
